import { useCallback, useState } from 'react';
import type { InspectionCapture, InspectionFlag } from '../api/inspections';
import { nextUnreviewedIndex, type ReviewItem } from './inspectionGroups';
import { useReviewShortcuts } from './useReviewShortcuts';

/** Which captures a review page shows: all of them, or the ones a question picks out. */
export type CaptureFilter = 'all' | 'pending' | 'problem' | 'flagged';

/** Whether the filter keeps the capture. A context card is kept by "all" alone. */
export function matchesFilter(capture: InspectionCapture, filter: CaptureFilter): boolean {
  switch (filter) {
    case 'pending':
      return !capture.reviewedBy;
    case 'problem':
      return capture.flags.includes('problem') || capture.agentVerdict === 'problem';
    case 'flagged':
      // What something other than a person already pointed at: the platform's
      // own reading of the picture, or the agent's note.
      return (capture.qcFlags?.length ?? 0) > 0 || !!capture.agentVerdict || !!capture.agentNote;
    default:
      return true;
  }
}

const ADVANCE_KEY = 'ops.review.advance';
/** How long a mark stays on screen before the viewer moves on: long enough to see which button took. */
export const ADVANCE_MS = 350;

function readAdvance(): boolean {
  try {
    return window.localStorage.getItem(ADVANCE_KEY) !== '0';
  } catch {
    return true;
  }
}

/**
 * What the two review pages share (one tour's, a whole campaign's): which
 * capture is open in the viewer, which the grid points at, the five flags and
 * the note of a capture, the 1-5/N keys, and the queue.
 *
 * The queue (the owner, 2026-09-29): the first mark on a capture nobody had
 * marked moves the viewer to the next one that waits, after a moment, so a
 * tour is reviewed with one key per picture. A second flag on the same
 * capture, or a change to one already reviewed, moves nothing: a person who
 * came back to a picture is looking at it. The switch is the person's and is
 * remembered in the browser.
 */
export function useReviewFlow(
  items: ReviewItem[],
  save: (uploadId: number, flags: InspectionFlag[], note: string) => void,
) {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [advance, setAdvanceState] = useState<boolean>(readAdvance);
  const [filter, setFilter] = useState<CaptureFilter>('all');

  const setAdvance = useCallback((on: boolean) => {
    setAdvanceState(on);
    try {
      window.localStorage.setItem(ADVANCE_KEY, on ? '1' : '0');
    } catch {
      // A browser that keeps nothing forgets the switch; the review works all the same.
    }
  }, []);

  const indexOf = useCallback(
    (uploadId: number) =>
      items.findIndex((it) => it.type === 'capture' && it.capture.uploadId === uploadId),
    [items],
  );

  const toggleFlag = useCallback(
    (uploadId: number, flag: InspectionFlag) => {
      const at = indexOf(uploadId);
      const item = at >= 0 ? items[at] : undefined;
      if (!item || item.type !== 'capture') return;
      const current = item.capture;
      const had = current.flags.includes(flag);
      const flags = had ? current.flags.filter((f) => f !== flag) : [...current.flags, flag];
      save(uploadId, flags, current.note ?? '');
      const first = !had && current.flags.length === 0 && !current.reviewedBy;
      if (first && advance && viewerIndex !== null) {
        const next = nextUnreviewedIndex(items, at);
        if (next !== null && next !== at) {
          window.setTimeout(
            // Only when the person is still on the picture they marked.
            () => setViewerIndex((now) => (now === at ? next : now)),
            ADVANCE_MS,
          );
        }
      }
    },
    [items, indexOf, save, advance, viewerIndex],
  );

  const saveNote = useCallback(
    (uploadId: number, note: string) => {
      const at = indexOf(uploadId);
      const item = at >= 0 ? items[at] : undefined;
      if (!item || item.type !== 'capture') return;
      if (note === (item.capture.note ?? '')) return;
      save(uploadId, item.capture.flags, note);
    },
    [items, indexOf, save],
  );

  // The grid's own "current" capture for the keys while the viewer is closed:
  // whatever a click or Tab last focused, or the first capture while nothing
  // has; computed inline so it is right from the render the captures arrive in.
  const gridIndex =
    focusedIndex !== null && items[focusedIndex]?.type === 'capture'
      ? focusedIndex
      : items.findIndex((it) => it.type === 'capture');
  const activeIndex = viewerIndex ?? (gridIndex >= 0 ? gridIndex : null);

  useReviewShortcuts(
    items,
    activeIndex,
    viewerIndex !== null,
    toggleFlag,
    setViewerIndex,
    setFocusedIndex,
  );

  /** Opens the viewer on the first capture that waits, or on the first one when none does. */
  const openQueue = useCallback(() => {
    const first = items.findIndex((it) => it.type === 'capture' && !it.capture.reviewedBy);
    const any = items.findIndex((it) => it.type === 'capture');
    if (first >= 0) setViewerIndex(first);
    else if (any >= 0) setViewerIndex(any);
  }, [items]);

  return {
    viewerIndex,
    setViewerIndex,
    focusedIndex,
    setFocusedIndex,
    gridIndex,
    advance,
    setAdvance,
    filter,
    setFilter,
    toggleFlag,
    saveNote,
    openQueue,
  };
}
